import { config } from '@/shared/config';
import { dateTimeFormat } from '@/shared/lib/intl';

export { useNow } from './useNow';

/**
 * The single date adapter (CLAUDE.md §9): Jalali for `fa`, Gregorian for `en`,
 * always rendered in clinic wall-clock time even though the API speaks UTC.
 */
export function formatDate(date: Date, locale: string): string {
  return dateTimeFormat(calendarLocale(locale), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: config.clinicTimezone,
  }).format(date);
}

export function formatWeekday(date: Date, locale: string): string {
  return dateTimeFormat(calendarLocale(locale), {
    weekday: 'long',
    timeZone: config.clinicTimezone,
  }).format(date);
}

/**
 * The calendar grid starts Saturday for `fa` and Monday for `en`. The stored
 * weekday index stays canonical (ISO) in the API (CLAUDE.md §9).
 */
export function firstWeekdayOfLocale(locale: string): number {
  return locale === 'fa' ? 6 : 1;
}

/**
 * The seven ISO weekdays in the order the active locale displays them: fa
 * starts Saturday, en starts Monday. The value on the wire always stays
 * canonical ISO (1 = Monday … 7 = Sunday) — this is the one place the two
 * orders meet (CLAUDE.md §9).
 */
export function weekdayOrderForLocale(locale: string): readonly number[] {
  const first = firstWeekdayOfLocale(locale);

  return Array.from({ length: 7 }, (_unused, offset) => ((first - 1 + offset) % 7) + 1);
}

/**
 * The localized name of an ISO weekday. Derived from a reference Monday so the
 * names come from Intl rather than a hand-written table that would need
 * translating twice.
 */
export function weekdayLabel(isoWeekday: number, locale: string): string {
  // 2024-01-01 is a Monday; noon UTC keeps it the same date in Tehran
  const monday = new Date(Date.UTC(2024, 0, 1, 12, 0, 0));

  return formatWeekday(addDays(monday, isoWeekday - 1), locale);
}

/** `2026-08-01` in clinic time — the shape the API and the URL both use. */
export function toClinicIsoDate(date: Date): string {
  const parts = dateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: config.clinicTimezone,
  }).format(date);

  return parts;
}

/** Just the day number — Jalali for `fa` — for the calendar strip. */
export function formatDayNumber(date: Date, locale: string): string {
  return dateTimeFormat(calendarLocale(locale), {
    day: 'numeric',
    timeZone: config.clinicTimezone,
  }).format(date);
}

export function formatWeekdayShort(date: Date, locale: string): string {
  return dateTimeFormat(calendarLocale(locale), {
    weekday: 'short',
    timeZone: config.clinicTimezone,
  }).format(date);
}

/** Just the month name — «مرداد» for `fa` — for the day cards in the strip. */
export function formatMonthName(date: Date, locale: string): string {
  return dateTimeFormat(calendarLocale(locale), {
    month: 'long',
    timeZone: config.clinicTimezone,
  }).format(date);
}

export function formatMonthLabel(date: Date, locale: string): string {
  return dateTimeFormat(calendarLocale(locale), {
    month: 'long',
    year: 'numeric',
    timeZone: config.clinicTimezone,
  }).format(date);
}

/** The hour on the clinic's wall clock, which is the only hour that matters. */
export function clinicHour(date: Date): number {
  const hour = dateTimeFormat('en-US', {
    hour: '2-digit',
    hour12: false,
    timeZone: config.clinicTimezone,
  }).format(date);

  return Number.parseInt(hour, 10);
}

/**
 * An instant that is unambiguously *inside* the given clinic day, whatever the
 * reader's own timezone. 09:00 UTC is midday in Tehran and still the same
 * calendar date anywhere from UTC-8 to UTC+14, so day arithmetic built on it
 * cannot slip across a midnight.
 */
export function clinicDayFromIso(isoDate: string): Date {
  return new Date(`${isoDate}T09:00:00Z`);
}

/** ISO weekday of a clinic date: 1 = Monday … 7 = Sunday, as the API speaks. */
export function isoWeekdayOf(date: Date): number {
  const day = new Date(`${toClinicIsoDate(date)}T00:00:00Z`).getUTCDay();

  return day === 0 ? 7 : day;
}

/**
 * The year, month and day *of the reader's calendar* — Jalali for `fa`. The
 * month grid has to be built in the calendar the reader sees, otherwise a
 * Persian month would be drawn with Gregorian boundaries (CLAUDE.md §9).
 */
export function calendarParts(date: Date, locale: string): CalendarParts {
  const parts = dateTimeFormat(calendarLocale(locale), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    numberingSystem: 'latn',
    timeZone: config.clinicTimezone,
  }).formatToParts(date);

  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((entry) => entry.type === type)?.value ?? 0);

  return { year: part('year'), month: part('month'), day: part('day') };
}

/**
 * The other direction: a day the reader *named* in their own calendar, back to
 * the instant the API speaks in.
 *
 * `calendarParts` is `Intl`, and `Intl` only converts one way. Rather than take
 * on a Jalali library — for one form — this searches: guess a Gregorian date,
 * read back what calendar it lands on, and step by the gap until it matches.
 * Two or three iterations settle it, then a short scan covers the leap-year
 * edges where a 30-day month becomes 29.
 *
 * Returns null when the day does not exist — 31 Esfand, 31 April — which is a
 * real answer the caller has to handle, not an error.
 */
export function fromCalendarParts(parts: CalendarParts, locale: string): Date | null {
  const { year, month, day } = parts;

  if (!Number.isInteger(year) || month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  // A Jalali year begins around 21 March of `year + 621`; a Gregorian one is
  // simply itself. Either way this only has to be close.
  let cursor =
    locale === 'fa'
      ? new Date(Date.UTC(year + 621, 2, 21, 9, 0, 0))
      : new Date(Date.UTC(year, month - 1, day, 9, 0, 0));

  for (let attempt = 0; attempt < 6; attempt += 1) {
    const current = calendarParts(cursor, locale);
    const gap = (year - current.year) * 365 + (month - current.month) * 30 + (day - current.day);

    if (gap === 0) {
      break;
    }

    cursor = addDays(cursor, gap);
  }

  // The estimate lands within a few days; walking outwards finds the exact one
  // and, when it finds nothing, proves the day does not exist.
  for (let offset = 0; offset <= 40; offset += 1) {
    for (const candidate of offset === 0
      ? [cursor]
      : [addDays(cursor, offset), addDays(cursor, -offset)]) {
      const found = calendarParts(candidate, locale);

      if (found.year === year && found.month === month && found.day === day) {
        return candidate;
      }
    }
  }

  return null;
}

/**
 * How many days a month holds in the reader's calendar — 29, 30 or 31.
 *
 * Asked rather than tabulated: a Jalali leap year is what decides whether
 * Esfand has 29 days or 30, and the rule for that is exactly the thing a
 * hand-written table gets wrong every fourth year.
 */
export function daysInCalendarMonth(year: number, month: number, locale: string): number {
  for (const length of [31, 30, 29]) {
    if (fromCalendarParts({ year, month, day: length }, locale) !== null) {
      return length;
    }
  }

  return 28;
}

/** The first day of the month `date` falls in, in the reader's calendar. */
export function startOfCalendarMonth(date: Date, locale: string): Date {
  return addDays(date, 1 - calendarParts(date, locale).day);
}

/**
 * One month forward or back. Stepping 32 days from a month's first day always
 * lands inside the next month — Jalali months run 29 to 31 days — and snapping
 * back to that month's start is what makes the jump calendar-correct.
 */
export function shiftCalendarMonth(date: Date, locale: string, direction: 1 | -1): Date {
  const start = startOfCalendarMonth(date, locale);

  return startOfCalendarMonth(addDays(start, direction === -1 ? -1 : 32), locale);
}

/**
 * The cells of a month grid: whole weeks in the locale's reading order, padded
 * with the neighbouring months' days so every row holds seven.
 */
export function monthGridDays(anchor: Date, locale: string): readonly CalendarCell[] {
  const monthStart = startOfCalendarMonth(anchor, locale);
  const month = calendarParts(monthStart, locale).month;
  const lead = (isoWeekdayOf(monthStart) - firstWeekdayOfLocale(locale) + 7) % 7;
  const gridStart = addDays(monthStart, -lead);

  const cells = Array.from({ length: 6 * 7 }, (_unused, offset): CalendarCell => {
    const date = addDays(gridStart, offset);

    return {
      date,
      iso: toClinicIsoDate(date),
      inMonth: calendarParts(date, locale).month === month,
    };
  });

  // a short month can leave a trailing week entirely outside it; drawing an
  // empty row would only add noise
  const weeks = Math.ceil((lead + monthLength(monthStart, locale)) / 7);

  return cells.slice(0, weeks * 7);
}

/** Calendar days, not 24-hour blocks — DST would drift the second kind. */
export function addDays(date: Date, days: number): Date {
  const shifted = new Date(date.getTime());
  shifted.setDate(shifted.getDate() + days);

  return shifted;
}

export function isSameClinicDay(left: Date, right: Date): boolean {
  return toClinicIsoDate(left) === toClinicIsoDate(right);
}

/** The booking horizon as a list of days, starting today in clinic time. */
export function clinicDayRange(from: Date, days: number): readonly Date[] {
  return Array.from({ length: days }, (_unused, offset) => addDays(from, offset));
}

function calendarLocale(locale: string): string {
  return locale === 'fa' ? 'fa-IR-u-ca-persian' : 'en-US';
}

/** How many days the month starting at `monthStart` holds, 29 through 31. */
function monthLength(monthStart: Date, locale: string): number {
  const month = calendarParts(monthStart, locale).month;
  let length = 28;

  while (calendarParts(addDays(monthStart, length), locale).month === month) {
    length += 1;
  }

  return length;
}

/** Year, month and day as the reader's calendar counts them. */
export interface CalendarParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** One cell of a month grid. `inMonth` is false for the padding days. */
export interface CalendarCell {
  readonly date: Date;
  readonly iso: string;
  readonly inMonth: boolean;
}

/**
 * The same day as `formatDate`, written as numbers a spreadsheet can sort:
 * `1405/05/24` for `fa`, `2026-08-15` for `en`.
 *
 * Three deliberate differences from `formatDate`, and each one exists because
 * this string ends up in a cell rather than on a screen:
 *
 *   • **the month is a number, not a name.** «۲۴ مرداد ۱۴۰۵» has no order a
 *     spreadsheet can find — sorting a column of those groups every اسفند
 *     together and every مرداد together, which is the wrong answer in the one
 *     place the operator most needs the right one.
 *   • **western digits.** Persian numerals sort by the collation of whatever
 *     locale Excel happens to be running in, and against a western-digit
 *     column they cannot be compared at all.
 *   • **zero padded, largest unit first.** That is what makes plain text sort
 *     chronologically: `1405/05/24` precedes `1405/12/01` as a string exactly
 *     as it does as a date.
 *
 * The calendar stays the reader's (§9) — a Persian date, just spelled in a
 * form the tool can order.
 */
export function formatNumericDate(date: Date, locale: string): string {
  const { year, month, day } = calendarParts(date, locale);
  const separator = locale === 'fa' ? '/' : '-';
  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${String(year).padStart(4, '0')}${separator}${pad(month)}${separator}${pad(day)}`;
}
